import React, { useMemo } from 'react';
import { Check } from 'lucide-react';
import { ElementsRenderer } from '../../shared';
import ReadingComprehensionElement from './ReadingComprehensionElement';
import { cn } from '../../ui/utils';

const getQuestionPlainText = (elements = []) =>
  elements
    .map((el) => (el.html ? el.html.replace(/<[^>]+>/g, ' ') : ''))
    .join(' ')
    .toLowerCase();

const McqQuestionBody = ({
  questionId,
  elements,
  answer,
  options = [],
  errors,
  updateAndSaveAnswer,
  readingComprehensionQuestionRef
}) => {
  const isMultipleChoice = useMemo(
    () => getQuestionPlainText(elements).includes('select all that apply'),
    [elements]
  );

  const selectedSingle = answer?.[0] || null;
  const selectedMultiple = useMemo(() => {
    if (!Array.isArray(answer)) return [];
    return answer.filter(Boolean);
  }, [answer]);

  const isSelected = (optionId) =>
    isMultipleChoice ? selectedMultiple.includes(optionId) : selectedSingle === optionId;

  const handleSelect = (optionId) => {
    if (isMultipleChoice) {
      const next = selectedMultiple.includes(optionId)
        ? selectedMultiple.filter((id) => id !== optionId)
        : [...selectedMultiple, optionId];
      updateAndSaveAnswer(questionId, next);
      return;
    }

    updateAndSaveAnswer(questionId, optionId);
  };

  return (
    <div className="katex-question w-full space-y-4 md:space-y-6">
      {readingComprehensionQuestionRef && (
        <ReadingComprehensionElement questionRef={readingComprehensionQuestionRef} />
      )}

      <div className="question-container space-y-3 md:space-y-4">
        <div
          className="text-base leading-relaxed text-foreground md:text-lg [&_pre]:max-w-full [&_pre]:whitespace-pre-wrap [&_pre]:break-words [&_pre]:border-0 [&_pre]:bg-transparent [&_pre]:p-0"
          id="q-text"
        >
          <ElementsRenderer elements={elements} />
        </div>
        {isMultipleChoice && (
          <p className="text-sm italic text-muted-foreground">Select all that apply:</p>
        )}
      </div>

      <div className="space-y-3">
        {options.map(({ id, elements: optionElements }) => {
          const selected = isSelected(id);

          return (
            <div
              key={id}
              role="button"
              tabIndex={0}
              onClick={() => handleSelect(id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleSelect(id);
                }
              }}
              aria-pressed={selected}
              className={cn(
                'flex w-full cursor-pointer items-start rounded-lg border p-4 text-left transition-colors',
                selected
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              )}
            >
              {isMultipleChoice ? (
                <div
                  className={cn(
                    'mr-3 mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-[4px] border',
                    selected
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-muted-foreground bg-input-background'
                  )}
                  aria-hidden
                >
                  {selected && <Check className="h-3 w-3" />}
                </div>
              ) : (
                <div
                  className={cn(
                    'mr-3 mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2',
                    selected ? 'border-primary bg-primary' : 'border-muted-foreground'
                  )}
                  aria-hidden
                >
                  {selected && <div className="h-2 w-2 rounded-full bg-white" />}
                </div>
              )}
              <div className="flex-1 leading-relaxed text-foreground">
                <ElementsRenderer elements={optionElements} />
              </div>
            </div>
          );
        })}
      </div>

      {errors?.answer?.message && (
        <p className="text-sm text-destructive" role="alert">
          {errors.answer.message}
        </p>
      )}
    </div>
  );
};

export default McqQuestionBody;
